import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useConferenceEdit } from '@/module/Conference/model/ConferenceQuery';
import {
  ConferenceSchema,
  conferenceSchema,
  defaultConferenceValues,
} from '@/module/Conference/model/ConferenceSchema';

import { useAppNotification } from '@feature/utils';

import { ConferenceForm } from './ConferenceForm';
import { Conference } from './model/ConferenceTypes';

interface ConferenceEditProps {
  conference: Conference;
}

const convert = (conference: Conference): ConferenceSchema => ({
  ...conference,
  confStartDate: conference.confStartDate,
  confEndDate: conference.confEndDate,
});

export function ConferenceEdit(props: ConferenceEditProps) {
  const { conference } = props;
  const { t } = useTranslation();
  const { mutate, isPending, isError, isSuccess } = useConferenceEdit(conference.id);

  const notification = useAppNotification();
  const onSubmit = (values: ConferenceSchema) => {
    mutate(values, {
      onError: notification.error,
      onSuccess: notification.success,
    });
  };

  const values = useMemo(() => convert(conference), [conference]);
  const navigate = useNavigate();

  return (
    <ConferenceForm
      title={t('conferences.form.title.edit')}
      schema={conferenceSchema}
      defaultValues={defaultConferenceValues}
      isError={isError}
      isSuccess={isSuccess}
      values={values}
      onSubmit={onSubmit}
      isSubmitting={isPending}
      onCancel={() => navigate({ to: '..' })}
    />
  );
}
